'use client';
import { createContext, useContext, useState, useEffect } from 'react';
import type { DisplayRates } from '@/lib/api';
import { LANG_CURRENCY } from '@/lib/currency';

interface PriceContextValue {
  displayRates: DisplayRates;
  convert: (usd: number | null) => string | null;
  lang: string;
}

const PriceContext = createContext<PriceContextValue>({
  displayRates: {},
  convert: () => null,
  lang: 'en',
});

export function PriceProvider({ rates, initialLang, children }: { rates: DisplayRates; initialLang?: string; children: React.ReactNode }) {
  // Seed with the server-resolved language (from the `lang` cookie) so the first
  // render already matches the site language — no flash of English before mount.
  const [lang, setLang] = useState<string>(initialLang ?? 'en');

  useEffect(() => {
    const read = () => {
      try {
        const stored = localStorage.getItem('lang');
        if (stored) return stored;
        // No explicit choice yet: the home page is ISR-cached (revalidate=120)
        // so server-side host/header detection never sees per-visitor state —
        // check the real browser hostname instead (sprintbox rewrites Host
        // server-side before forwarding to origin, but the browser's address
        // bar — window.location — always reflects the real ru.gamessales.com
        // the visitor actually connected to). Persisted so it sticks across
        // navigations (per user 2026-08-26: ru. edge should show ru/RUB immediately).
        if (window.location.hostname.startsWith('ru.gamessales.com')) {
          localStorage.setItem('lang', 'ru');
          return 'ru';
        }
        return initialLang ?? 'en';
      } catch { return initialLang ?? 'en'; }
    };
    setLang(read());
    const handler = () => setLang(read());
    window.addEventListener('lang-change', handler);
    return () => window.removeEventListener('lang-change', handler);
  }, [initialLang]);

  function convert(usd: number | null): string | null {
    if (usd == null || usd <= 0) return null;
    const currency = LANG_CURRENCY[lang] ?? 'USD';
    const rate = rates[currency];
    if (!rate || currency === 'USD') return null; // USD shows as-is via fmtPrice
    const value = usd * rate.value * (1 + rate.markup / 100);
    return `${Math.round(value).toLocaleString(rate.locale)} ${rate.label}`;
  }

  return (
    <PriceContext.Provider value={{ displayRates: rates, convert, lang }}>
      {children}
    </PriceContext.Provider>
  );
}

export function usePriceConvert() {
  return useContext(PriceContext);
}
